import "reflect-metadata";
import "imagica-corekit/dist/base/cutil/Extensions";
import "@uikit/func";

import { AuthStore } from "imagica-corekit/dist/base/store/AuthStore";

import { UpgradeSuccess } from "@uiview/pages/upgradeSuccess/UpgradeSuccess";
import { PlansList } from "@uiview/pages/PlansList";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { HashRouter as Router, Routes, Route, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { message, Button, Popconfirm } from "antd";
import { HomeOutlined, EditOutlined } from "@ant-design/icons";
import { Provider, useDispatch, useSelector } from "react-redux";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import UI from "./views/UI";
import Studio from "./views/Studio";
import BuildLayerView from "./views/BuildLayerView";
import SharedCanvasView from "./views/thinking-layout-editor/SharedCanvasView";
import { ErrorBoundary } from "./views/ErrorBoundary";
import store from "./store";
import { fotActions } from "./store/fot";
import { editorActions } from "./store/editor";
import { WithRouterLogin as Login } from "@views/login/Login";
import DomainBasedPermission from "./views/DomainBasedPermission";
import NotFound from "./views/NotFound";
import ActivateError from "./views/ActivateError";
import DocumentMeta from "./views/components/DocumentMeta";
import { logEvent } from "@uikit/service/amplitude";
import { ExamplePage } from "./uiview/pages/example/ExamplePage";
import { RestClientPageBloc } from "./uiview/pages/example/RestClientPageBloc";
import { RestClientPage } from "./uiview/pages/example/RestClientPage";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";
import LoadingOutline from "./views/components/LoadingOutline";
import { SubscriptionPage } from "@uiview/pages/profile/subscription";
import { getIt } from "@uikit/getIt";
import { FotAuthStore } from "imagica-corekit/dist/base/store/FotAuthStore";
import { useEventbus } from "imagica-uikit/dist/hooks/useEventbus";
import { UnauthorizedMsg } from "imagica-corekit/dist/base/msg/UnauthorizedMsg";
import { ProfilePage } from "@uiview/pages/profile/ProfilePage";
import { AccountPage } from "@uiview/pages/profile/account/AccountPage";
import PasswordPage from "@uiview/pages/profile/password/PasswordPage";
import { Editor } from "@uiview/pages/editor/Editor";
import { PreferencesStore } from "imagica-corekit/dist/base/store/PreferencesStore";
import { SubscriptionStore } from "imagica-corekit/dist/cases/store/SubscriptionStore";
import { useInitPage } from "@custom-hooks/useInitPage";
import { HomePluginStore } from "imagica-corekit/dist/base/store/HomePluginStore";
import { useStore as useImagincStore } from "imagica-uikit/dist/hooks/useStore";

dayjs.extend(duration);
dayjs.extend(relativeTime);

const root = ReactDOM.createRoot(document.getElementById("root"));

const DeniedPage = () => {
  return (
    <div className="permission-denied">
      <p>Permission Denied</p>
    </div>
  );
};

const Root = () => {
  const homePluginStore = getIt(HomePluginStore);
  const homePluginState = useImagincStore(homePluginStore).value;
  const location = useLocation();

  message.config({
    maxCount: 1,
  });
  window.onbeforeunload = function () {
    localStorage.removeItem("studioMode");
    localStorage.removeItem("testMode");
  };
  const isMobile = window.navigator.userAgent.includes("Mobile");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { init, isInitLoading, clickLogOut } = useInitPage();

  const fetching = useSelector(state => state.fot.fetching);
  const mode = useSelector(state => state.fot.mode);
  const isLogin = useSelector(state => state.fot.isLogin);
  const showShareSignin = useSelector(state => state.fot.showShareSignin);

  const setMe = val => {
    dispatch(editorActions.setMe(val));
  };

  const setIsLogin = useCallback(
    val => {
      dispatch(fotActions.setIsLogin(val));
    },
    [dispatch]
  );

  const setLoginMethod = val => {
    dispatch(editorActions.setLoginMethod(val));
  };

  const hideStudioBtnPaths = ["/layer", "/editor"];
  const handleHomeButtonClick = () => {
    dispatch(fotActions.setMode("productionMode"));
    navigate("/");
  };

  const selectedTemplate = useSelector(state => state.studio.selectedTemplate);

  const handleStudioButtonClick = () => {
    dispatch(fotActions.setMode("studioMode"));
    navigate("/studio");
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Anton: useEventbus throws an error */
  useEventbus(UnauthorizedMsg, () => {
    console.log("--------------------------- UnauthorizedMsg");
    clickLogOut();
  });

  return (
    <div>
      <DocumentMeta isLogin={isLogin} isShare={homePluginState.isShare} shareProjectName={selectedTemplate.name} />
      {!isMobile && !hideStudioBtnPaths.some(x => location.pathname.includes(x)) ? (
        <DomainBasedPermission>
          <div>
            {mode === "productionMode" ? (
              <Button
                className="Home-Button"
                size="large"
                icon={<EditOutlined />}
                onClick={handleStudioButtonClick}
                disabled={fetching}
              >
                Studio
              </Button>
            ) : (
              <Button
                className="Home-Button"
                size="large"
                icon={<HomeOutlined />}
                onClick={handleHomeButtonClick}
                disabled={fetching}
              >
                Home
              </Button>
            )}
            <div className="log-out">
              <Popconfirm
                placement="bottomRight"
                title="Log out"
                onConfirm={() => clickLogOut()}
                okText="Yes"
                cancelText="No"
              >
                <a className="iconfont icon-sign_out"></a>
              </Popconfirm>
            </div>
          </div>
        </DomainBasedPermission>
      ) : null}
      {isInitLoading.value ? (
        <LoadingOutline />
      ) : !isLogin && !showShareSignin ? (
        <>
          <Routes>
            <Route path="/signin" element={<Login setIsLogin={setIsLogin} isSignUp={false} />} />
            <Route path="/signup" element={<Login setIsLogin={setIsLogin} isSignUp={true} />} />
            <Route path="*" element={<Login setIsLogin={setIsLogin} isSignUp={false} />} />
          </Routes>
        </>
      ) : (
        <div className="route-box">
          <Routes>
            <Route path="/ExamplePage" element={<ExamplePage></ExamplePage>}></Route>
            <Route
              path="/RestClientPage"
              element={<RestClientPage bloc={new RestClientPageBloc()}></RestClientPage>}
            ></Route>
            <Route
              path="/editor"
              element={<Editor page="home" clickLogOut={clickLogOut} share={homePluginState.isShare} />}
            >
              {profileRouteConfig()}
            </Route>
            <Route path={"/editor/:attrIndexer"} element={<Editor clickLogOut={clickLogOut} />}>
              {profileRouteConfig()}
            </Route>
            <Route
              path="/editor/shared_intelligent_app/:projectId"
              element={<Editor clickLogOut={clickLogOut} share={true} />}
            ></Route>
            <Route path="/editor/shared_canvas" element={<SharedCanvasView />}></Route>
            <Route path={"/editor/error"} element={<ActivateError />} />
            <Route path={"/editor/denied-tip"} element={<DeniedPage />} />

            <Route
              path="/layer"
              element={
                <DomainBasedPermission>
                  <BuildLayerView clickLogOut={clickLogOut} />
                </DomainBasedPermission>
              }
            ></Route>
            <Route
              path="/studio"
              element={
                <DomainBasedPermission>
                  <ErrorBoundary>
                    {" "}
                    <Studio />{" "}
                  </ErrorBoundary>
                </DomainBasedPermission>
              }
            ></Route>
            <Route
              path="/"
              element={
                <DomainBasedPermission>
                  <UI UIMode="productionMode" clickLogOut={clickLogOut} />
                </DomainBasedPermission>
              }
            ></Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      )}
    </div>
  );
};

root.render(
  <GoogleOAuthProvider clientId={settings.googleOauthClientId}>
    <Provider store={store}>
      <Router>
        <Root />
      </Router>
    </Provider>
  </GoogleOAuthProvider>
);

const profileRouteConfig = () => {
  return (
    <>
      <Route path="profile" element={<ProfilePage />}>
        <Route path="account" element={<AccountPage />}></Route>
        <Route path="password" element={<PasswordPage />}></Route>
        <Route path="subscription" element={<SubscriptionPage />}>
          <Route path="upgrade_plans" element={<PlansList />}></Route>
          <Route path="upgrade_success" element={<UpgradeSuccess />}></Route>
        </Route>
      </Route>
      <Route path="upgrade_plans" element={<PlansList />}></Route>
      <Route path="upgrade_success" element={<UpgradeSuccess />}></Route>
    </>
  );
};

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
